import de from "date-fns/esm/locale/de";
import {Timestamp} from "firebase/firestore";

export const DATE_FORMAT = "dd MMM yyyy";
export const LOCAL = de;

/**
 * Adds leading zeros to a number
 * @param num the number to which leading zeros are added
 * @param totalLength of the returned string
 * @returns
 */
function addLeadingZeros(num: number, totalLength: number): string {
  return String(num).padStart(totalLength, "0");
}

/**
 * Converts date object to german string representation.
 * @param date
 * @returns
 */
export function dateToStr(date: Timestamp | undefined): string {
  if (date === undefined) {
    return "---";
  }
  const d = date.toDate();
  return addLeadingZeros(d.getDate(), 2) + "." + addLeadingZeros(d.getMonth() + 1, 2) + "." + d.getFullYear();
}

/**
 * Converts a date object to UTC milliseconds.
 * @param date
 * @returns
 */
export function dateToUTC(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/**
 * Is the given date within the interval (exclusive)
 * @param dirtyDate
 * @param startTime
 * @param endTime
 * @returns
 */
export function isWithinIntervalExclusive(dirtyDate: number, startTime: number, endTime: number) {
  if (!(startTime <= endTime)) {
    throw new RangeError("Invalid interval");
  }
  return dirtyDate > startTime && dirtyDate < endTime;
}

/**
 * Calculates the number of days between two dates.
 * @param startDate
 * @param endDate
 * @returns
 */
export function daysBetween(startDate: Date | number, endDate: Date | number) {
  // The number of milliseconds in all UTC days (no DST)
  const oneDay = 1000 * 60 * 60 * 24;
  // A day in UTC always lasts 24 hours (unlike in other time formats)
  let start = 0;
  if (startDate instanceof Date) {
    start = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  } else {
    start = startDate;
  }

  let end = 0;
  if (endDate instanceof Date) {
    end = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  } else {
    end = endDate;
  }

  // so it's safe to divide by 24 hours
  return (start - end) / oneDay;
}

/**
 *
 * @deprecated use timestamp.toDate()
 * @param timestamp
 * @returns
 */
export function timestampToDate(timestamp: Timestamp): Date {
  return new Date(timestamp.seconds * 1000);
}

/**
 * Removes the hour component from the date.
 * @param timestamp
 * @returns
 */
export function timestampNoHours(timestamp: Timestamp): Timestamp {
  const date = new Date(timestamp.seconds * 1000);
  return dateToTimestamp(date);
}

/**
 * Converts a date object to a Firestore Timestamp.
 * @param date
 * @returns
 */
export function dateToTimestamp(date: Date): Timestamp {
  return Timestamp.fromMillis(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

/**
 * Gets the first day of the previous month.
 * @returns
 */
export function getFirstDayOfPreviousMonth(): Timestamp {
  const today = new Date();
  let year = today.getFullYear();
  let month = today.getMonth();

  // Check if the current month is January (0)
  if (month === 0) {
    month = 11; // Set to December (11)
    year -= 1; // Decrease the year by 1
  } else {
    month -= 1; // Just decrease the month by 1
  }

  // Create a new date for the first day of the previous month
  const firstDayOfPreviousMonth = new Date(year, month, 1);
  return Timestamp.fromDate(firstDayOfPreviousMonth);
}


